/**
 * Single source of truth for supported native languages.
 *
 * English name (`value`) is what we store and submit.
 * `label` is the autonym shown in learner-facing UI.
 * `locale` maps to i18n locale codes.
 */

export const NATIVE_LANGUAGE_DEFINITIONS = [
  { value: 'Spanish', label: 'Español', locale: 'es' },
  { value: 'Arabic', label: 'العربية', locale: 'ar' },
  { value: 'Chinese', label: '中文', locale: 'zh_CN' },
  { value: 'Japanese', label: '日本語', locale: 'ja' },
  { value: 'Korean', label: '한국어', locale: 'ko' },
  { value: 'Amharic', label: 'አማርኛ', locale: 'am' },
  { value: 'French', label: 'Français', locale: 'fr' },
  { value: 'Portuguese', label: 'Português', locale: 'pt' },
  { value: 'Haitian Creole', label: 'Kreyòl Ayisyen', locale: 'ht_HT' },
  { value: 'Farsi', label: 'فارسی', locale: 'fa-latn-ir' },
  { value: 'Russian', label: 'Русский', locale: 'ru' },
  { value: 'Tagalog', label: 'Tagalog', locale: 'tl' },
  { value: 'Ukrainian', label: 'Українська', locale: 'uk' },
  { value: 'Turkish', label: 'Türkçe', locale: 'tr' },
  { value: 'Somali', label: 'Soomaali', locale: 'so' },
  { value: 'Vietnamese', label: 'Tiếng Việt', locale: 'vi' },
  { value: 'Pashto', label: 'پښتو', locale: 'ps_AF' },
  { value: 'Burmese', label: 'မြန်မာစာ', locale: 'my' },
  { value: 'Punjabi', label: 'ਪੰਜਾਬੀ', locale: 'pa' },
  { value: 'Swahili', label: 'Kiswahili', locale: 'sw' },
  { value: 'Lao', label: 'ພາສາລາວ', locale: 'lo_LA' },
  { value: 'Chuukese', label: 'Fosun Chuuk', locale: 'chk' },
  { value: 'Nepali', label: 'नेपाली', locale: 'ne' },
  { value: 'English', label: 'English', locale: 'en' },
] as const

export type NativeLanguage = (typeof NATIVE_LANGUAGE_DEFINITIONS)[number]['value']

export const NATIVE_LANGUAGES: NativeLanguage[] = NATIVE_LANGUAGE_DEFINITIONS.map((language) => language.value)

export const NATIVE_LANGUAGE_LABELS: Record<NativeLanguage, string> = Object.fromEntries(
  NATIVE_LANGUAGE_DEFINITIONS.map(({ value, label }) => [value, label])
) as Record<NativeLanguage, string>

export const LANGUAGE_TO_LOCALE: Record<NativeLanguage, string> = Object.fromEntries(
  NATIVE_LANGUAGE_DEFINITIONS.map(({ value, locale }) => [value, locale])
) as Record<NativeLanguage, string>

/** Combobox options: English name with autonym alongside for admin recognition. */
export const NATIVE_LANGUAGE_OPTIONS = NATIVE_LANGUAGE_DEFINITIONS.map(({ value, label }) => ({
  value,
  label: value === label ? value : `${value} (${label})`,
}))
